import React, { useEffect, useMemo } from "react";
import { observer } from "mobx-react";
import { Route, Redirect, useHistory, useLocation } from "react-router-dom";

import BaronaLogoSvg from "images/barona-logo.svg";
import Heading from "./components/Heading";
import Country from "./components/Country";
import JobLocations from "./components/JobLocations";
import YourEmail from "./components/YourEmail";
import NameAndPassword from "./components/NameAndPassword";
import YourName from "./components/YourName";
import JobTitles from "./components/JobTitles";
import InterestAreas from "./components/InterestAreas";
import Skills from "./components/Skills";
import SeniorityLevel from "./components/SeniorityLevel";
import { Wrapper, Overlay, OverlayInfo } from "./styled";
import { useQueryParams } from "../../../utils/hooks";
import useStore from "../../contexts/store";
import { Spinner } from "../../components/Core";
import { useTranslation } from "../../hooks/useTranslation";
import { useLanguageAndCountryUrl, useSetCountryAndLocale } from "../../hooks";

const OnboardingFlow = ({ countryPaths, isMultiLanguageBrand }) => {
  const { shorterFlow, return_to: returnTo, application_id: applicationId, isExternalTypeJob } = useQueryParams();
  const languageAndLanguage = useLanguageAndCountryUrl();
  const { pathname } = useLocation();
  const history = useHistory();
  const { user } = useStore("User");
  const { global_brand: globalBrand } = useStore("initialState");
  const { t, changeLanguage } = useTranslation();

  const locale = useMemo(() => {
    const match = pathname.match(/^\/([a-z]{2})\/([a-z]{2})\b/i);

    if (match && match[2]) {
      return match[2];
    }

    return "";
  }, [pathname]);

  const isChangeLanguage = useMemo(
    () => globalBrand.enforce_locale_based_paths && user?.ui_locale && user.ui_locale !== locale && locale,
    [globalBrand.enforce_locale_based_paths, user?.ui_locale, locale]
  );

  const buildPath = path => {
    if (languageAndLanguage) {
      return `${languageAndLanguage}${path}`;
    }

    return path;
  };

  const countryAndLanguage = useMemo(() => {
    const { pathname } = history.location;
    const [country, lang] = pathname.split("/").slice(1, 3);

    const currentCountry = globalBrand?.countries.find(item =>
      item.country_code ? item.country_code.toLowerCase() === country : {}
    );
    const currentLanguage = currentCountry ? currentCountry.translations.find(item => item.locale === lang) : {};

    return { country: currentLanguage?.locale?.toUpperCase(), language: currentLanguage.name };
  }, []);

  useEffect(() => {
    if (!user && globalBrand.custom_authentication_b2c_url) {
      window.location.href = globalBrand.custom_authentication_b2c_url;
    }

    if (user && user.onboarding_steps_completed) {
      history.push(`/home${history.location.pathname}`);
    }

    if (returnTo) {
      const prevOnboardingForm = JSON.parse(sessionStorage.getItem("onboardingFlow")) || {};
      const patternApplicationId = /application_id=(\d+)/;
      const returnApplicationId = returnTo.includes("application_id") ? returnTo.match(patternApplicationId)[1] : "";

      const normalizeReturnTo = isExternalTypeJob
        ? returnTo
        : returnApplicationId || applicationId
          ? `/jobs/${returnApplicationId || applicationId}/application`
          : returnTo || "/home";

      const onboardingForm = {
        ...prevOnboardingForm,
        returnTo: normalizeReturnTo,
        applicationId: returnApplicationId,
        isExternalTypeJob
      };

      sessionStorage.setItem("onboardingFlow", JSON.stringify(onboardingForm));
    }

    if (applicationId) {
      const prevOnboardingForm = JSON.parse(sessionStorage.getItem("onboardingFlow")) || {};

      const onboardingForm = {
        ...prevOnboardingForm,
        returnTo: prevOnboardingForm.returnTo || `/jobs/${applicationId}/application`,
        applicationId
      };

      sessionStorage.setItem("onboardingFlow", JSON.stringify(onboardingForm));
    }

    if (shorterFlow === "true") {
      const savedSearch = history.location.search || isExternalTypeJob ? history.location.search : "";
      history.push(buildPath(`/onboarding/your-email${savedSearch}`));

      const prevOnboardingForm = JSON.parse(sessionStorage.getItem("onboardingFlow")) || {};

      const onboardingForm = {
        ...prevOnboardingForm,
        isShorterFlow: true
      };

      sessionStorage.setItem("onboardingFlow", JSON.stringify(onboardingForm));
    }
  }, []);

  useEffect(() => {
    if (isChangeLanguage) {
      changeLanguage(locale);
    }
  }, [user?.ui_locale, locale]);

  useSetCountryAndLocale(history, isChangeLanguage);

  return (
    <Wrapper>
      {!user && globalBrand.custom_authentication_b2c_url && (
        <Overlay>
          <Spinner size="50px" margin="auto" />
          <BaronaLogoSvg />
          <OverlayInfo>{t("login.redirect_text")}</OverlayInfo>
        </Overlay>
      )}

      <Heading
        isHideLink={globalBrand.custom_authentication_b2c_url}
        isMultiLanguageBrand={isMultiLanguageBrand}
        countryAndLanguage={countryAndLanguage}
        globalBrand={globalBrand}
      />
      <Route
        exact
        path={countryPaths
          ?.map(({ slug: countryLanguagePath }) => ["/onboarding", `/${countryLanguagePath}/onboarding`])
          .flat()}
        render={() => <Redirect to={buildPath("/onboarding/country")} />}
      />
      <Route
        exact
        path={countryPaths
          ?.map(({ slug: countryLanguagePath }) => [
            "/onboarding/country",
            `/${countryLanguagePath}/onboarding/country`
          ])
          .flat()}
        render={() => <Country buildPath={buildPath} />}
      />
      <Route
        exact
        path={countryPaths
          ?.map(({ slug: countryLanguagePath }) => [
            "/onboarding/job-locations",
            `/${countryLanguagePath}/onboarding/job-locations`
          ])
          .flat()}
        render={() => <JobLocations buildPath={buildPath} />}
      />
      <Route
        exact
        path={countryPaths
          ?.map(({ slug: countryLanguagePath }) => [
            "/onboarding/job-titles",
            `/${countryLanguagePath}/onboarding/job-titles`
          ])
          .flat()}
        render={() => <JobTitles buildPath={buildPath} />}
      />
      <Route
        exact
        path={countryPaths
          ?.map(({ slug: countryLanguagePath }) => [
            "/onboarding/interest-areas",
            `/${countryLanguagePath}/onboarding/interest-areas`
          ])
          .flat()}
        render={() => <InterestAreas buildPath={buildPath} />}
      />
      <Route
        exact
        path={countryPaths
          ?.map(({ slug: countryLanguagePath }) => ["/onboarding/skills", `/${countryLanguagePath}/onboarding/skills`])
          .flat()}
        render={() => <Skills buildPath={buildPath} />}
      />
      <Route
        exact
        path={countryPaths
          ?.map(({ slug: countryLanguagePath }) => [
            "/onboarding/seniority-level",
            `/${countryLanguagePath}/onboarding/seniority-level`
          ])
          .flat()}
        render={() => <SeniorityLevel buildPath={buildPath} locale={locale} />}
      />
      <Route
        exact
        path={countryPaths
          ?.map(({ slug: countryLanguagePath }) => [
            "/onboarding/your-email",
            `/${countryLanguagePath}/onboarding/your-email`
          ])
          .flat()}
        render={() => <YourEmail buildPath={buildPath} />}
      />
      <Route
        exact
        path={countryPaths
          ?.map(({ slug: countryLanguagePath }) => [
            "/onboarding/name-and-password",
            `/${countryLanguagePath}/onboarding/name-and-password`
          ])
          .flat()}
        render={() => <NameAndPassword buildPath={buildPath} locale={locale} />}
      />
      <Route
        exact
        path={countryPaths
          ?.map(({ slug: countryLanguagePath }) => [
            "/onboarding/your-name",
            `/${countryLanguagePath}/onboarding/your-name`
          ])
          .flat()}
        render={() => <YourName buildPath={buildPath} locale={locale} />}
      />
    </Wrapper>
  );
};

export default observer(OnboardingFlow);
